/**
 * PURPOSE: Administrar la programación por fases, incluyendo la llave fija del evento 17.
 * RESPONSIBILITIES: Consultar, publicar, editar y visualizar partidos respetando idtypephase para el evento 17.
 * COLLABORATORS: ModalProgramacion, servicios de matches/config_category y componentes de visualización.
 * ROUTE: frontend/src/area/ProgramacionArea/ProgramacionArea.jsx
 */

// src/area/ProgramacionArea/ProgramacionArea.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import API_BASE_URL from "../../config/config.js";
import Bandera2 from "../../components/Bandera/Bandera2";
import ModalProgramacion from "../../components/Modals/ModalProgramacion/ModalProgramacion";

import { Tab } from "@headlessui/react";
import { TbShirtFilled } from "react-icons/tb";
import { GiWinterGloves } from "react-icons/gi";
import { TbCircleLetterLFilled } from "react-icons/tb";
import { FaSocks } from "react-icons/fa6";

import "./ProgramacionArea.css";
import moment from "moment";
import { format } from "date-fns";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";

import ConfirmAssignModal from "./ConfirmAssignModal.jsx";
// FTR-0041: asignación inteligente de camisetas (solo con el modelo de opciones activo).
import AssignUniformsModal from "./AssignUniformsModal.jsx";
// FTR-0046: "Asignar camisetas" (y sus versiones) solo para roles 1 y 4; el botón se ve igual para todos.
import { canUseUniformAssignment } from "../../utils/authUser.js";
import DesignacionesTab from "./DesignacionesTab.jsx";
import VenueSelect from "../../components/Inputs/VenueSelect.jsx";
import { listHayllisV2Venues } from "../../services/hayllisV2VenueService.js";
import { getParticipantLabel } from "../../utils/participantDisplayName.js";
import { loadUniformOptionsStatus, useUniformOptionsStatus } from "../../hooks/useUniformOptionsStatus.js";
// FTR-0028 WG6: la cancha y el slot estructural solo existen con el Centro de Programación activo.
import { useProgrammingCenterStatus } from "../../hooks/useProgrammingCenterStatus.js";
import { bulkAssignMatchUniforms } from "../../services/uniformOptionsService.js";
import { getUniformErrorMessage } from "../../admin/AdminCompany/uniformOptions.utils.js";
import {
  buildInlineDraft,
  computeDirtyEntries,
  isFieldDirty,
  buildBulkJornadaPayload,
  resolveBulkSelection,
} from "../../utils/programacionInlineEdit.js";
import { createMatchNumberResolver } from "../../utils/programacionMatchNumber.js";
// FTR-0040: orden canónico compartido por la vista normal y la filtrada (el N° nunca se renumera).
import {
  buildTournamentPrintedNumbers,
  groupRowsByVenue,
  isByeMatchRecord,
  orderFetchedMatches,
} from "../../utils/programacionTableOrder.js";
import {
  buildInstitutionOptions,
  filterMatchesByInstitutions,
  groupMatchesByStage,
} from "../../utils/programacionInstitutionFilter.js";
import InstitutionMultiSelect from "./InstitutionMultiSelect.jsx";

function parseAsLocalDate(f) {
  if (typeof f === "string" && /^\d{4}-\d{2}-\d{2}$/.test(f)) {
    const [y, m, d] = f.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  return new Date(f);
}

// Mismo predicado de siempre, ahora compartido con el orden canónico de la tabla (FTR-0040).
const isByeMatch = isByeMatchRecord;

function formatFecha(fecha) {
  if (!fecha) return "";
  const d = parseAsLocalDate(fecha);
  const diasAbreviados = ["Do.", "Lu.", "Ma.", "Mie.", "Jue.", "Vie.", "Sab."];
  return `${diasAbreviados[d.getDay()]} ${format(d, "dd")}/${format(d, "MM")}`;
}

const ProgramacionArea = () => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedDate, setSelectedDate] = useState("1");
  const [nroFechasGrupo, setNroFechasGrupo] = useState(0);
  const [nroEtapasFinal, setNroEtapasFinal] = useState(0);
  const [moodName, setMoodName] = useState(1);
  const [useBandera, setUseBandera] = useState(false);
  const [isHaveRepechaje, setIsHaveRepechaje] = useState(false);

  const [fechas, setFechas] = useState([]);
  const [etapas, setEtapas] = useState([]);
  const [matches, setMatches] = useState([]);

  const [sport, setSport] = useState(null);
  const { idevent, idsport } = useParams();

  const [open, setOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);

  const [loading, setLoading] = useState(true);
  const [dots, setDots] = useState("");

  const [publishedValue, setPublishedValue] = useState("0"); // "0" | "1"
  const [savingPublished, setSavingPublished] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const [selectedTipoUbi, setSelectedTipoUbi] = useState(1);

  const [localParam, setLocalParam] = useState(2); // 2 = Oscuro para Local; 1 = Oscuro para Visitante
  const [isAssigning, setIsAssigning] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  // FTR-0041: modal de la asignación inteligente (reemplaza a los radios "Oscuro" con el flag activo).
  const [autoAssignOpen, setAutoAssignOpen] = useState(false);

  const [tableView, setTableView] = useState(false);
  const [readyToFetchMatches, setReadyToFetchMatches] = useState(false);

  // Vista secundaria "Programación | Designaciones" (FTR-0001): Programación sigue siendo la vista
  // por defecto; Designaciones vive dentro de esta misma área, no como módulo de navegación nuevo.
  const [view, setView] = useState("programacion");

  // FTR-0007: edición inline de la vista tabla + actualización masiva de la jornada.
  const [inlineEdit, setInlineEdit] = useState(false);
  const [inlineDraft, setInlineDraft] = useState({});
  // FTR-0014: con opciones normalizadas activas (o mientras se consulta) el uniforme no se edita como texto aquí.
  const uniformOptions = useUniformOptionsStatus();
  const uniformTextReadOnly = uniformOptions.loading || uniformOptions.enabled;
  // FTR-0041: con el modelo de opciones confirmado hay un solo flujo de asignación masiva (el inteligente).
  const smartUniformsEnabled = !uniformOptions.loading && uniformOptions.enabled === true;
  const authUser = useSelector((state) => state.auth?.user);
  const [savingInline, setSavingInline] = useState(false);
  const [bulkDate, setBulkDate] = useState("");
  const [bulkVenueId, setBulkVenueId] = useState(null);
  const [applyingBulk, setApplyingBulk] = useState(false);
  // FTR-0033: partidos (idmatch) seleccionados en "Editar tabla" para acotar la acción masiva.
  const [selectedMatchIds, setSelectedMatchIds] = useState([]);
  const [venueOptions, setVenueOptions] = useState([]);
  const [venueContextLoaded, setVenueContextLoaded] = useState(false);
  // FTR-0028 WG6: la columna "Cancha" aparece solo cuando el backend confirma el Centro encendido
  // (fail closed, igual que el resto: la interfaz nunca lee un flag de compilación).
  const programmingCenter = useProgrammingCenterStatus();
  const courtsEnabled = programmingCenter.enabled;

  // FTR-0040: filtro por instituciones de la vista tabla. Vive en su propio estado: `matches` sigue siendo
  // la jornada que alimenta las acciones de jornada; el torneo completo solo se usa para la vista filtrada.
  const [selectedInstitutionIds, setSelectedInstitutionIds] = useState([]);
  const [tournamentMatches, setTournamentMatches] = useState([]);
  const [tournamentLoading, setTournamentLoading] = useState(false);
  const [tournamentLoaded, setTournamentLoaded] = useState(false);
  const [filterParticipants, setFilterParticipants] = useState([]);
  const [filterParticipantsLoaded, setFilterParticipantsLoaded] = useState(false);
  const tournamentRequestRef = useRef(0);
  const institutionFilterActive = tableView && selectedInstitutionIds.length > 0;

  const getCurrentIdTypePhase = () => {
    if (nroEtapasFinal > 0 && nroFechasGrupo === 0) return 2;
    return selectedIndex === 0 ? 1 : 2;
  };

  const getCurrentPhaseLabel = () =>
    getCurrentIdTypePhase() === 1 ? "Fase de Grupos" : "Fase de Eliminación";

  useEffect(() => {
    if (matches && matches.length > 0) {
      const v = matches[0]?.published ?? 0;
      setPublishedValue(String(Number(v)));
    } else {
      setPublishedValue("0");
    }
  }, [matches, selectedDate, selectedIndex]);

  const openConfirmIfAllowed = () => {
    // FTR-0007: las acciones masivas exigen resolver primero los cambios inline pendientes.
    if (inlineEdit && computeDirtyEntries(matches, inlineDraft).length > 0) {
      toast("Guarda o descarta los cambios de la tabla antes de asignar camisetas.", {
        icon: "⚠️",
      });
      return;
    }

    if (String(publishedValue) === "1") {
      toast("No permitido: la programación está en estado OFICIAL. Cambia a BORRADOR para continuar.", {
        icon: "🚫",
      });
      return;
    }

    if (!matches || matches.length === 0) {
      toast("No hay partidos en esta vista.", { icon: "⚠️" });
      return;
    }

    setConfirmOpen(true);
  };

  const handleAssignUniforms = async () => {
    if (!matches || matches.length === 0) {
      toast("No hay partidos en esta vista.", { icon: "⚠️" });
      return;
    }

    setIsAssigning(true);

    try {
      const body = {
        matches: matches.map((m) => m.idmatch),
        local: Number(localParam),
        idsport: Number(idsport),
      };

      // FTR-0014: con opciones normalizadas, la masiva asigna por ID desde las opciones de cada equipo.
      if ((await loadUniformOptionsStatus()).enabled) {
        const result = await bulkAssignMatchUniforms({ ...body, idevent: Number(idevent) });
        toast.success(`Uniformes asignados: ${result.updated ?? 0}/${result.attempted ?? matches.length}`);
        if (result.warnings?.length) {
          toast(`${result.warnings.length} lado(s) sin opción registrada para el tono pedido.`, { icon: "⚠️" });
        }
        await getMatches(getCurrentIdTypePhase(), selectedDate);
        return;
      }

      const { data } = await axios.put(`${API_BASE_URL}/uniformsOfMatchs`, body);

      const ok = data?.updated ?? 0;
      const total = data?.attempted ?? matches.length;

      toast.success(`Camisetas asignadas: ${ok}/${total}`);

      await getMatches(getCurrentIdTypePhase(), selectedDate);
    } catch (e) {
      console.error(e);
      toast.error(getUniformErrorMessage(e, "No se pudo asignar camisetas"));
    } finally {
      setIsAssigning(false);
    }
  };

  // FTR-0041: la asignación inteligente abarca todo el torneo; los partidos publicados o que no están por
  // jugarse simplemente no son elegibles (lo decide el backend), así que el estado OFICIAL de la jornada
  // visible no bloquea. Sí exige resolver antes los cambios pendientes de la tabla.
  const openAutoAssignIfAllowed = () => {
    // FTR-0046: el botón se ve igual para todos los roles, pero solo los roles 1 y 4 entran al modal (el backend
    // también lo exige).
    if (!canUseUniformAssignment(authUser)) {
      toast.error("No tienes permiso para Asignar camisetas. Solo un administrador (rol 1 o 4) puede ingresar.");
      return;
    }
    if (inlineEdit && computeDirtyEntries(matches, inlineDraft).length > 0) {
      toast("Guarda o descarta los cambios de la tabla antes de asignar camisetas.", { icon: "⚠️" });
      return;
    }
    setAutoAssignOpen(true);
  };

  const handleAutoAssignApplied = async () => {
    setTournamentLoaded(false);
    await getMatches(getCurrentIdTypePhase(), selectedDate);
  };

  const handleSavePublished = async () => {
    try {
      setSavingPublished(true);

      const idtypephase = getCurrentIdTypePhase();

      await axios.patch(`${API_BASE_URL}/patchprogramacionpublished`, {
        idevent: Number(idevent),
        idsport: Number(idsport),
        idtypephase,
        nro_fecha: Number(selectedDate),
        published: Number(publishedValue),
      });

      toast.success("¡Programación Publicada Correctamente!");
    } catch (e) {
      console.error(e);
      alert("No se pudo actualizar el estado de publicación.");
    } finally {
      setSavingPublished(false);
    }
  };

  const handleDownloadProgramacionAdmin = async () => {
    setIsDownloading(true);

    try {
      const url =
        selectedIndex === 1
          ? Number(idevent) === 197 || Number(idevent) === 212
            ? `${API_BASE_URL}/programacionfasefinaldivpdf`
            : `${API_BASE_URL}/programacionfasefinalpdf`
          : `${API_BASE_URL}/programacionpdf`;

      let payload;

      if (selectedIndex === 1) {
        payload = {
          idevent,
          idsport,
          selectedIndex,
          selectedEliminationStage: String(selectedDate),
        };

        if (Number(idevent) === 197 || (Number(idevent) === 212 && selectedDate != "1")) {
          payload.tipo_ubi = selectedTipoUbi;
        }

        payload.isFromHayllis = false;
      } else {
        payload = {
          idevent,
          idsport,
          selectedIndex,
          selectedGroupDate: String(selectedDate),
          selectedEliminationStage: String(selectedDate),
          isFromHayllis: true,
        };
      }

      console.log("payload", payload);

      const { data, headers } = await axios.post(url, payload, {
        responseType: "arraybuffer",
      });

      if (!(headers["content-type"] ?? "").startsWith("application/pdf")) {
        alert("Error: el servidor no pudo generar el PDF");
        return;
      }

      const disp = headers["content-disposition"];
      let filename = "programacion.pdf";

      if (disp) {
        const m = disp.match(/filename="?([^";]+)"?/);
        if (m) filename = m[1];
      }

      const blob = new Blob([data], { type: "application/pdf" });
      const blobURL = URL.createObjectURL(blob);
      const a = document.createElement("a");

      a.href = blobURL;
      a.download = filename;
      a.click();

      URL.revokeObjectURL(blobURL);
    } catch (e) {
      console.error(e);
      alert("No se pudo generar el PDF");
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    if (!loading) return;

    const frames = ["", ".", "..", "..."];
    let idx = 0;

    const interval = setInterval(() => {
      idx = (idx + 1) % frames.length;
      setDots(frames[idx]);
    }, 250);

    return () => clearInterval(interval);
  }, [loading]);

  useEffect(() => {
    let cancelled = false;

    const getConfigCategory = async () => {
      const response = await axios.post(`${API_BASE_URL}/config_category`, {
        idevent,
        idsport,
      });

      const data = response.data;
      const fechasGrupo = Number(data.nro_fechas_grupo || 0);
      const etapasFinal = Number(data.nro_etapas_final || 0);

      if (cancelled) return;

      setNroFechasGrupo(fechasGrupo);
      setNroEtapasFinal(etapasFinal);
      setMoodName(data.moodName);
      setUseBandera(data.useBandera);
      setIsHaveRepechaje(data.isHaveRepechaje || false);

      setFechas(Array.from({ length: fechasGrupo }, (_, i) => String(i + 1)));
      setEtapas(["Octavos", "Cuartos", "Semis", "Final"].slice(4 - etapasFinal));
    };

    const getSport = async () => {
      const sportResponse = await axios.get(`${API_BASE_URL}/sport/?idsport=${idsport}`);

      if (cancelled) return;

      setSport(sportResponse.data);
    };

    const bootstrap = async () => {
      if (!idevent || !idsport) return;

      setReadyToFetchMatches(false);
      setLoading(true);
      setMatches([]);
      setSelectedIndex(0);
      setSelectedDate("1");
      // FTR-0040: el filtro de instituciones no sobrevive a un cambio de evento o deporte.
      tournamentRequestRef.current += 1;
      setSelectedInstitutionIds([]);
      setTournamentMatches([]);
      setTournamentLoaded(false);
      setTournamentLoading(false);
      setFilterParticipants([]);
      setFilterParticipantsLoaded(false);

      try {
        await Promise.all([getConfigCategory(), getSport()]);

        if (!cancelled) {
          setReadyToFetchMatches(true);
        }
      } catch (error) {
        console.error("Error cargando configuración de programación:", error);

        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, [idevent, idsport]);

  const getMatches = async (idtypephase, nro_fecha) => {
    setLoading(true);

    try {
      const requestBody = {
        idevent,
        idsport,
        nro_fecha,
        idtypephase,
      };

      const typeSport = Number(sport?.typeSport);
      const hasFinalPhaseConfigured = Number(nroEtapasFinal) > 0;

      const isEvent17Bracket = Number(idevent) === 17;

      // Evento 17 usa una llave real con idtypephase = 2 también para vóley.
      // El resto de eventos conserva la compatibilidad anterior.
      const shouldUseOldNoPhaseFilter =
        !isEvent17Bracket &&
        (typeSport === 3 || (typeSport === 2 && !hasFinalPhaseConfigured));

      if (shouldUseOldNoPhaseFilter) {
        delete requestBody.idtypephase;
      }

      const response = await axios.post(`${API_BASE_URL}/getmatches`, requestBody);

      const fetched = Array.isArray(response.data) ? response.data : [];

      // FTR-0040: mismo orden de siempre (hora, sin hora al final, BYE al final) con un pre-orden por
      // idmatch, para que los empates no dependan del orden de filas del backend (no hay ORDER BY).
      const nextMatches = orderFetchedMatches(fetched);
      setMatches(nextMatches);
      return nextMatches;
    } catch (error) {
      console.error("Error al obtener los matches:", error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!readyToFetchMatches) return;
    if (!sport) return;

    getMatches(getCurrentIdTypePhase(), selectedDate);
  }, [
    readyToFetchMatches,
    selectedIndex,
    selectedDate,
    nroFechasGrupo,
    nroEtapasFinal,
    idevent,
    idsport,
    sport?.typeSport,
  ]);

  const groupLabel = (groupId) => String.fromCharCode(64 + groupId);

  /**
   * FTR-0028 WG6: un partido materializado por el Centro antes del sorteo no tiene participantes,
   * pero SÍ sabe quién lo jugará según la estructura: `idgrupo` + `nro_team1/2` son "A1" y "A4".
   * `getmatches` ya devolvía esas tres columnas, así que esto no necesita nada del backend. Cuando
   * el participante existe, manda el participante: el slot es solo el respaldo de "Por Definir".
   */
  const matchSlotLabel = (match, side) => {
    const groupId = Number(match?.idgrupo);
    const slot = Number(side === 1 ? match?.nro_team1 : match?.nro_team2);
    if (!Number.isInteger(groupId) || groupId <= 0) return null;
    if (!Number.isInteger(slot) || slot <= 0) return null;
    return `${groupLabel(groupId)}${slot}`;
  };

  const renderSideName = (match, side) => {
    const participant = side === 1 ? match?.Participant1 : match?.Participant2;
    if (participant?.Institution) return getParticipantLabel(participant, moodName);
    return matchSlotLabel(match, side) || "Por Definir";
  };

  // FTR-0028 WG6: canchas activas por sede, tal como las publica el catálogo V2 (vacío sin el flag).
  const courtsByVenue = new Map(
    (venueOptions || []).map((venue) => [Number(venue.idsede ?? venue.id), venue.courts || []])
  );

  const courtOptionsForMatch = (match) => courtsByVenue.get(Number(match?.sede?.idsede)) || [];

  const courtNameForMatch = (match) => {
    const assigned = match?.Cancha?.name;
    if (assigned) return assigned;
    const courtId = Number(match?.idcancha);
    if (!Number.isInteger(courtId) || courtId <= 0) return "";
    const found = courtOptionsForMatch(match).find((court) => Number(court.id) === courtId);
    return found?.name || "";
  };

  // ───────── FTR-0007: edición inline de la tabla + acción masiva de jornada ─────────

  const inlineDirtyEntries = inlineEdit ? computeDirtyEntries(matches, inlineDraft) : [];
  const hasUnsavedInline = inlineDirtyEntries.length > 0;

  // Paridad exacta con getMatches: cuándo la consulta de la jornada filtra por fase.
  const shouldIncludePhaseInScope = () => {
    const typeSport = Number(sport?.typeSport);
    const hasFinalPhaseConfigured = Number(nroEtapasFinal) > 0;
    const isEvent17Bracket = Number(idevent) === 17;
    const shouldUseOldNoPhaseFilter =
      !isEvent17Bracket &&
      (typeSport === 3 || (typeSport === 2 && !hasFinalPhaseConfigured));
    return !shouldUseOldNoPhaseFilter;
  };

  const confirmDiscardInline = () => {
    if (!hasUnsavedInline) return true;
    return window.confirm("Hay cambios sin guardar en la tabla. ¿Descartarlos?");
  };

  // Sale del modo edición; también limpia la selección (cancelar, guardar, cambio de fase/fecha/etapa/vista).
  const exitInlineEdit = () => {
    setInlineEdit(false);
    setInlineDraft({});
    setSelectedMatchIds([]);
  };

  // Envuelve un cambio de jornada/vista: pide confirmación si hay cambios sin guardar.
  const guardInlineThen = (action) => {
    if (!confirmDiscardInline()) return;
    if (inlineEdit) exitInlineEdit();
    action();
  };

  const startInlineEdit = () => {
    setInlineDraft(buildInlineDraft(matches));
    setSelectedMatchIds([]);
    setInlineEdit(true);
  };

  // FTR-0033: la selección usa idmatch, nunca el N° impreso (que se reparte en orden de render).
  const toggleMatchSelection = (idmatch) => {
    setSelectedMatchIds((prev) =>
      prev.includes(idmatch) ? prev.filter((id) => id !== idmatch) : [...prev, idmatch]
    );
  };

  const handleCancelInline = () => {
    if (!confirmDiscardInline()) return;
    exitInlineEdit();
  };

  const onInlineCellChange = (idmatch, field, value) => {
    setInlineDraft((prev) => ({
      ...prev,
      [idmatch]: { ...prev[idmatch], [field]: value },
    }));
  };

  const handleSaveInline = async () => {
    const entries = computeDirtyEntries(matches, inlineDraft);
    if (entries.length === 0) {
      toast("No hay cambios para guardar.", { icon: "ℹ️" });
      return;
    }

    setSavingInline(true);
    try {
      // Un solo lote atómico: todos los partidos editados o ninguno.
      await axios.patch(`${API_BASE_URL}/patchprogramacionmatchesbatch`, {
        matches: entries,
      });
      toast.success("Cambios guardados");
      exitInlineEdit();
      await getMatches(getCurrentIdTypePhase(), selectedDate);
    } catch (e) {
      console.error("Error guardando cambios inline:", e);
      // Todo o nada: el backend revirtió todo; se conservan los cambios para corregirlos.
      toast.error(
        e?.response?.data?.message ||
        "No se guardó ningún cambio. Corrige e intenta de nuevo."
      );
    } finally {
      setSavingInline(false);
    }
  };

  // Contexto de sedes (compañía del campeonato -> catálogo V2), igual que ModalProgramacion.
  useEffect(() => {
    let cancelled = false;

    const loadVenueContext = async () => {
      try {
        const eventResponse = await axios.get(
          `${API_BASE_URL}/championship/?idevent=${Number(idevent)}`
        );
        const idcompany = Number(eventResponse.data?.idcompany) || null;
        const availableVenues = idcompany
          ? await listHayllisV2Venues({ idevent, idcompany })
          : [];
        if (!cancelled) {
          setVenueOptions(availableVenues);
          setVenueContextLoaded(true);
        }
      } catch (error) {
        console.error("Error cargando sedes para la jornada:", error);
        if (!cancelled) setVenueContextLoaded(true);
      }
    };

    if (tableView && !venueContextLoaded && idevent) loadVenueContext();

    return () => {
      cancelled = true;
    };
  }, [tableView, venueContextLoaded, idevent]);

  // ───────── FTR-0040: filtro por instituciones (solo vista tabla, solo frontend) ─────────

  // Instituciones participantes del evento + deporte, igual que la pestaña Instituciones.
  useEffect(() => {
    let cancelled = false;

    const loadFilterParticipants = async () => {
      try {
        const response = await axios.post(`${API_BASE_URL}/getparticipants`, {
          idevent: Number(idevent),
          idsport: Number(idsport),
          idtypephase: 1,
        });
        if (!cancelled) {
          setFilterParticipants(Array.isArray(response.data) ? response.data : []);
          setFilterParticipantsLoaded(true);
        }
      } catch (error) {
        console.error("Error cargando instituciones para el filtro:", error);
        if (!cancelled) setFilterParticipantsLoaded(true);
      }
    };

    if (tableView && !filterParticipantsLoaded && idevent && idsport) loadFilterParticipants();

    return () => {
      cancelled = true;
    };
  }, [tableView, filterParticipantsLoaded, idevent, idsport]);

  // Todos los partidos del torneo (todas las fases y fechas/etapas): getmatches sin nro_fecha ni fase.
  // Las claves se OMITEN: enviar null filtraría `IS NULL`. Solo la última respuesta se aplica.
  const loadTournamentMatches = async () => {
    const requestId = ++tournamentRequestRef.current;
    setTournamentLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/getmatches`, { idevent, idsport });
      if (requestId !== tournamentRequestRef.current) return null;
      const fetched = Array.isArray(response.data) ? response.data : [];
      setTournamentMatches(fetched);
      setTournamentLoaded(true);
      return fetched;
    } catch (error) {
      console.error("Error al obtener los partidos del torneo:", error);
      if (requestId === tournamentRequestRef.current) {
        toast.error("No se pudieron cargar los partidos del torneo.");
      }
      return null;
    } finally {
      if (requestId === tournamentRequestRef.current) setTournamentLoading(false);
    }
  };

  const clearInstitutionFilter = () => {
    tournamentRequestRef.current += 1;
    setSelectedInstitutionIds([]);
    setTournamentLoading(false);
  };

  const handleInstitutionSelectionChange = (ids) => {
    const next = [...new Set(ids.map(Number).filter((id) => Number.isInteger(id) && id > 0))];
    const wasActive = selectedInstitutionIds.length > 0;
    if (!next.length) {
      clearInstitutionFilter();
      return;
    }
    setSelectedInstitutionIds(next);
    // Al activar el filtro, el torneo se recarga siempre: la vista filtrada nunca usa datos viejos.
    if (!wasActive) loadTournamentMatches();
  };

  // Abrir el selector carga el torneo si aún no está: así las opciones incluyen a toda institución
  // presente en algún partido, aunque su fila no venga en getparticipants.
  const handleInstitutionMenuOpen = () => {
    if (!tournamentLoaded && !tournamentLoading) loadTournamentMatches();
  };

  const institutionOptions = useMemo(
    () => buildInstitutionOptions({ participants: filterParticipants, matches: tournamentMatches, moodName }),
    [filterParticipants, tournamentMatches, moodName]
  );

  // Tras guardar un partido: la jornada se refresca como siempre; el torneo, si el filtro está activo.
  const refreshAfterMatchSave = () => {
    getMatches(getCurrentIdTypePhase(), selectedDate);
    if (institutionFilterActive) loadTournamentMatches();
    else setTournamentLoaded(false);
  };

  const filterLockTitle = "Quita el filtro de instituciones para usar las acciones de la jornada.";

  const hasBulkValues = String(bulkDate).trim() !== "" || Number(bulkVenueId) > 0;

  // FTR-0008: los BYE/descanso no cuentan para habilitar la acción masiva
  // (el backend también los excluye del alcance autoritativo).
  const realJornadaMatches = matches.filter((m) => !isByeMatch(m));

  // FTR-0008: la acción masiva se ejecuta de inmediato, sin modal de confirmación.
  const handleApplyBulk = async () => {
    if (hasUnsavedInline) {
      toast("Guarda o descarta los cambios de la tabla antes de aplicar la acción masiva.", {
        icon: "⚠️",
      });
      return;
    }
    if (!realJornadaMatches.length) {
      toast("La jornada seleccionada no tiene partidos.", { icon: "⚠️" });
      return;
    }
    if (!hasBulkValues) {
      toast("Debes indicar una fecha y/o una sede para aplicar a la jornada.", { icon: "⚠️" });
      return;
    }
    const built = buildBulkJornadaPayload({
      idevent,
      idsport,
      idtypephase: getCurrentIdTypePhase(),
      includePhase: shouldIncludePhaseInScope(),
      nroFecha: selectedDate,
      date: bulkDate,
      idsede: bulkVenueId,
      // FTR-0033: solo una selección parcial en "Editar tabla" acota la acción; 0 o todos = toda la jornada.
      idmatches: inlineEdit ? resolveBulkSelection(selectedMatchIds, realJornadaMatches) : null,
    });
    if (built.error) {
      toast(built.error, { icon: "⚠️" });
      return;
    }

    setApplyingBulk(true);
    try {
      // El alcance real lo resuelve el backend con idevent/idsport/[idtypephase]/nro_fecha;
      // `idmatches` solo lo restringe. El backend devuelve `affected` para diagnóstico; la UI no lo muestra.
      await axios.patch(`${API_BASE_URL}/patchprogramacionjornada`, built.payload);
      toast.success("Jornada actualizada");
      setBulkDate("");
      setBulkVenueId(null);
      setSelectedMatchIds([]);
      const refreshedMatches = await getMatches(getCurrentIdTypePhase(), selectedDate);
      // Si se sigue en "Editar tabla", el borrador se vuelve a sembrar con los datos nuevos: si no, una fecha
      // cambiada por la acción masiva quedaría como cambio pendiente y "Guardar cambios" la revertiría.
      if (inlineEdit && refreshedMatches) setInlineDraft(buildInlineDraft(refreshedMatches));
    } catch (e) {
      console.error("Error aplicando cambios a la jornada:", e);
      // Todo o nada: nada cambió; se conservan los valores elegidos para reintentar.
      toast.error(e?.response?.data?.message || "No se aplicó ningún cambio a la jornada.");
    } finally {
      setApplyingBulk(false);
    }
  };

  const renderUniformeExclusivo = (u, ids) => {
    if (["5", "42", "6", "7", "50", "51"].includes(ids)) {
      return (
        <div className="flex justify-center items-center gap-1 text-sm">
          <GiWinterGloves />
          <span>{u || "-"}</span>
        </div>
      );
    }

    if (["16", "17"].includes(ids)) {
      return (
        <div className="flex justify-center items-center gap-1 text-sm">
          <TbCircleLetterLFilled />
          <span>{u || "-"}</span>
        </div>
      );
    }

    return null;
  };

  const renderUniformeMedias = (u, ids) => {
    if (+idevent === 1 && ["6", "7"].includes(ids)) {
      return (
        <div className="flex justify-center items-center gap-1 text-sm">
          <FaSocks />
          <span>{u || "-"}</span>
        </div>
      );
    }

    return null;
  };

  // `phaseIndex` = pestaña de fase (0 grupos, 1 final). La vista filtrada (FTR-0040) mezcla fases y pasa
  // la fase de la fila; la vista normal sigue usando la pestaña seleccionada.
  const getMatchGroupText = (match, phaseIndex = selectedIndex) => {
    if (match?.idgrupo) {
      if (sport && sport.typeSport === 1) {
        if (phaseIndex === 0 && +idevent !== 16 && +idevent !== 208) {
          return groupLabel(match.idgrupo);
        }

        return match.tag_final || groupLabel(match.idgrupo);
      }

      return match.tag_final || groupLabel(match.idgrupo);
    }

    return match.tag_final || "-";
  };

  const openMatchModal = (match) => {
    setSelectedMatch(match);
    setOpen(true);
  };

  useEffect(() => {
    if ((Number(idevent) === 197 || Number(idevent) === 212) && selectedIndex === 1) {
      setSelectedTipoUbi(selectedDate === "2" ? 1 : 3);
    }
  }, [idevent, selectedIndex, selectedDate]);

  const renderTipoUbiRadios = () => {
    if ((Number(idevent) !== 197 && Number(idevent) !== 212) || selectedIndex !== 1) return null;
    if (selectedDate !== "2" && selectedDate !== "3") return null;

    const isSemi = selectedDate === "2";

    const radios = isSemi
      ? [
        {
          value: 1,
          label: (
            <>
              PROGRAMACION SEMIFINAL - IDA
              <br />
              PROGRAMACION SEMIFINAL - POR EL 5° PUESTO
              <br />
              PROGRAMACION - 9° LUGAR y 11° LUGAR
            </>
          ),
        },
        {
          value: 2,
          label: (
            <>
              PROGRAMACION SEMIFINAL - VUELTA
              <br />
              PROGRAMACION - 5° LUGAR y 7° LUGAR
            </>
          ),
        },
      ]
      : [
        {
          value: 3,
          label: <>PROGRAMACION FINAL - IDA</>,
        },
        {
          value: 4,
          label: (
            <>
              PROGRAMACION FINAL - VUELTA
              <br />
              PROGRAMACION - 3° LUGAR
            </>
          ),
        },
      ];

    return (
      <fieldset className="w-full max-w-3xl mx-auto mt-3">
        <legend className="sr-only">Tipo de programación</legend>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {radios.map((r) => {
            const selected = selectedTipoUbi === r.value;

            return (
              <label
                key={r.value}
                className={[
                  "group relative flex items-start gap-3 cursor-pointer",
                  "rounded-xl border p-3 sm:p-4 shadow-sm transition",
                  selected
                    ? "border-blue-600 bg-blue-50 ring-1 ring-blue-200"
                    : "border-gray-200 bg-white hover:border-gray-300",
                ].join(" ")}
              >
                <input
                  type="radio"
                  name="tipoUbi"
                  value={r.value}
                  checked={selected}
                  onChange={() => setSelectedTipoUbi(r.value)}
                  className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
                />

                <div className="min-w-0">
                  <span className="block text-[13px] sm:text-sm font-semibold text-gray-900 leading-5 break-words">
                    {r.label}
                  </span>
                </div>

                <span className="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-transparent group-hover:ring-gray-200 transition" />
              </label>
            );
          })}
        </div>
      </fieldset>
    );
  };

  const renderLoadingCards = () => {
    return (
      <div className="w-full col-span-1 md:col-span-2 p-4">
        <h2 className="text-2xl font-semibold text-gray-700 mb-6 text-center">
          Cargando Enfrentamientos{dots}
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="animate-pulse bg-white rounded-lg shadow-lg p-6 space-y-4">
              <div className="h-6 bg-gray-200 rounded w-1/2"></div>
              <div className="h-4 bg-gray-200 rounded w-full"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderProgramacionCards = () => {
    if (loading) return renderLoadingCards();

    return matches
      .filter((m) => !isByeMatch(m))
      .map((match) => (
        <div
          key={match.idmatch}
          className="card bg-white p-6 rounded-lg shadow-lg cursor-pointer flex flex-col items-center relative"
          onClick={() => openMatchModal(match)}
        >
          {match.tag_extra && (
            <span className="absolute top-2 right-2 bg-indigo-600 text-white text-xs font-semibold uppercase px-2 py-0.5 rounded-md shadow">
              {match.tag_extra.toUpperCase()}
            </span>
          )}

          <div className="text-center mb-4">
            <span
              className={`relative inline-block px-3 py-1 rounded-lg font-bold uppercase ${match.flag_tag_resaltante
                ? 'bg-red-600 text-white before:content-["*"] before:absolute before:inset-0 before:rounded-lg before:border-4 before:border-red-400 before:animate-ping'
                : "bg-gray-200 text-gray-800"
                }`}
            >
              {match?.idgrupo
                ? sport && sport.typeSport === 1
                  ? selectedIndex === 0 && +idevent !== 16 && +idevent !== 208
                    ? `GRUPO ${groupLabel(match.idgrupo)}`
                    : match.tag_final
                  : match.tag_final
                : match.tag_final}
            </span>

            {match.description_match && (
              <span className="block mt-1 text-xs px-1 py-0.5 bg-gray-100 text-gray-600 rounded-md shadow-sm">
                {match.description_match}
              </span>
            )}
          </div>

          <div className="flex justify-around items-center mb-4 relative w-full">
            <div className="team_icon w-16 h-16 rounded-full flex justify-center items-center relative">
              {match.Participant1 ? (
                <>
                  <img
                    src={`${match.Participant1.Institution.path_base || ""}${match.Participant1.Institution.image_path || ""}`}
                    alt={match.Participant1.Institution.abrev || "Pendiente"}
                    className="img_logo w-12 h-12"
                  />
                  {useBandera && (
                    <Bandera2 src={match.Participant1.Institution.country.img_path || ""} alt="imagen" />
                  )}
                </>
              ) : (
                <div className="w-12 h-12 bg-gray-300 rounded-full flex justify-center items-center">
                  <span className="text-gray-500"></span>
                </div>
              )}
            </div>

            <span className="text-xl font-bold">vs</span>

            <div className="team_icon w-16 h-16 rounded-full flex justify-center items-center relative">
              {match.Participant2 ? (
                <>
                  <img
                    src={`${match.Participant2.Institution.path_base || ""}${match.Participant2.Institution.image_path || ""}`}
                    alt={match.Participant2.Institution.abrev || "Pendiente"}
                    className="img_logo w-12 h-12"
                  />
                  {useBandera && (
                    <Bandera2 src={match.Participant2.Institution.country.img_path || ""} alt="imagen" />
                  )}
                </>
              ) : (
                <div className="w-12 h-12 bg-gray-300 rounded-full flex justify-center items-center">
                  <span className="text-gray-500"></span>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-around items-center mb-4 w-full">
            <div className="text-center" style={{ maxWidth: "48%" }}>
              <div className="text-xl font-semibold text-gray-900">
                {renderSideName(match, 1)}
              </div>

              <div className="flex justify-center items-center gap-1 text-sm">
                <TbShirtFilled />
                <span>{match.uniforme1 || "-"}</span>
              </div>

              {renderUniformeExclusivo(match.uniforme_exclusivo1, idsport)}
              {renderUniformeMedias(match.uniforme_medias1, idsport)}
            </div>

            <span className="text-xl font-bold invisible">vs</span>

            <div className="text-center" style={{ maxWidth: "48%" }}>
              <div className="text-xl font-semibold text-gray-900">
                {renderSideName(match, 2)}
              </div>

              <div className="flex justify-center items-center gap-1 text-sm">
                <TbShirtFilled />
                <span>{match.uniforme2 || "-"}</span>
              </div>

              {renderUniformeExclusivo(match.uniforme_exclusivo2, idsport)}
              {renderUniformeMedias(match.uniforme_medias2, idsport)}
            </div>
          </div>

          <div className="text-3xl font-bold mb-1">
            {match.time_match ? moment(match.time_match, "HH:mm:ss").format("h:mm A") : "Hora"}
          </div>

          <div className="text-xl font-semibold mb-4">
            {match.date_match ? moment(match.date_match).format("DD/MM/YYYY") : "Fecha"}
          </div>

          <div className="text-lg font-semibold text-center">{match.sede?.name || "Sede"}</div>
          <div className="text-sm text-center">{match.sede?.address || "Dirección"}</div>
          {/* FTR-0028 WG6: la cancha solo aparece si la hay; sin el Centro encendido no llega nunca. */}
          {courtsEnabled && courtNameForMatch(match) ? (
            <div className="text-sm font-semibold text-center text-indigo-700">
              {courtNameForMatch(match)}
            </div>
          ) : null}
        </div>
      ));
  };

  const renderProgramacionTable = () => {
    const visibleMatches = matches.filter((m) => !isByeMatch(m));

    // FTR-0035: N° impreso. Grupos reparten el pool en orden de render; fase final usa su nro_match.
    const resolveMatchNumber = createMatchNumberResolver(visibleMatches);

    if (loading) {
      return (
        <div className="w-full rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-2xl font-semibold text-gray-700 text-center">
            Cargando Enfrentamientos{dots}
          </h2>

          <div className="mt-6 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-10 animate-pulse rounded-lg bg-slate-100" />
            ))}
          </div>
        </div>
      );
    }

    if (visibleMatches.length === 0) {
      return (
        <div className="w-full rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <p className="text-lg font-bold text-slate-700">No hay partidos para mostrar.</p>
          <p className="mt-1 text-sm text-slate-500">Selecciona otra fecha o etapa.</p>
        </div>
      );
    }

    // FTR-0040: una tabla por sede, con el mismo helper que calcula el N° de la vista filtrada.
    const sedeGroups = groupRowsByVenue(visibleMatches);

    return (
      <div className="w-full space-y-5">
        <div className="flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-sm font-black uppercase tracking-wide text-slate-700">
              Vista tabla de programación
            </h3>
          </div>

          {/* FTR-0007/FTR-0008: acciones de edición inline (sin contadores visibles) */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex w-fit items-center rounded-full bg-slate-200 px-3 py-1 text-xs font-black text-slate-700">
              {visibleMatches.length} partido{visibleMatches.length === 1 ? "" : "s"}
            </span>

            {!inlineEdit ? (
              <button
                type="button"
                onClick={startInlineEdit}
                className="inline-flex items-center justify-center rounded-md bg-[#2564eb] px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-600"
                title="Editar fecha, hora y uniformes directamente en la tabla"
              >
                Editar tabla
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleSaveInline}
                  disabled={savingInline || !hasUnsavedInline}
                  className="inline-flex items-center justify-center rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
                  title="Guarda todos los cambios en una sola operación (todo o nada)"
                >
                  {savingInline ? "Guardando…" : "Guardar cambios"}
                </button>
                <button
                  type="button"
                  onClick={handleCancelInline}
                  disabled={savingInline}
                  className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancelar
                </button>
              </>
            )}
          </div>
        </div>

        {/* FTR-0007: acción masiva de fecha/sede para toda la jornada */}
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide text-slate-600">
                Fecha para toda la jornada
              </label>
              <input
                type="date"
                value={bulkDate}
                onChange={(e) => setBulkDate(e.target.value)}
                className="mt-1 w-44 rounded-md border border-gray-300 px-2 py-1.5 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
              />
            </div>

            <div className="min-w-[220px] flex-1 lg:max-w-xs">
              <label className="block text-xs font-bold uppercase tracking-wide text-slate-600">
                Sede para toda la jornada
              </label>
              <VenueSelect
                inputId="bulk-jornada-venue"
                venues={venueOptions}
                value={bulkVenueId}
                onChange={(value) => setBulkVenueId(value || null)}
                placeholder="Sin cambio de sede"
                className="mt-1"
              />
            </div>

            <div>
              <button
                type="button"
                onClick={handleApplyBulk}
                disabled={applyingBulk || loading || !realJornadaMatches.length || !hasBulkValues || hasUnsavedInline}
                className="inline-flex items-center justify-center rounded-md bg-indigo-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-600 disabled:cursor-not-allowed disabled:opacity-50"
                title={
                  hasUnsavedInline
                    ? "Guarda o descarta los cambios de la tabla primero"
                    : "Aplicar a todos los partidos de la jornada"
                }
              >
                {applyingBulk ? "Aplicando…" : "Aplicar a la jornada"}
              </button>
            </div>
          </div>
        </div>

        {sedeGroups.map((group) => (
          <div
            key={group.key}
            className="w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg"
          >
            <div className="w-full overflow-x-auto">
              <table className="min-w-[980px] w-full border-collapse text-sm">
                <thead>
                  <tr style={{ backgroundColor: "#2564eb" }} className="text-white">
                    <th className="border border-blue-500 px-3 py-3 text-center text-xs font-black uppercase tracking-wide">
                      N°
                    </th>
                    <th className="border border-blue-500 px-3 py-3 text-center text-xs font-black uppercase tracking-wide">
                      Fecha
                    </th>
                    <th className="border border-blue-500 px-3 py-3 text-center text-xs font-black uppercase tracking-wide">
                      Hora
                    </th>
                    {/* FTR-0028 WG6: la cancha vive en la misma tabla que la sede y la hora; no hay
                        un segundo sistema de programación. Sin el Centro encendido la columna no
                        existe y la tabla es la de siempre. */}
                    {courtsEnabled && (
                      <th className="border border-blue-500 px-3 py-3 text-center text-xs font-black uppercase tracking-wide">
                        Cancha
                      </th>
                    )}
                    <th className="border border-blue-500 px-3 py-3 text-center text-xs font-black uppercase tracking-wide">
                      Grupo
                    </th>
                    <th className="border border-blue-500 px-3 py-3 text-center text-xs font-black uppercase tracking-wide">
                      Uniforme
                    </th>
                    <th
                      colSpan={3}
                      className="border border-blue-500 px-3 py-3 text-center text-xs font-black uppercase tracking-wide"
                    >
                      SEDE: {group.sedeName}
                    </th>
                    <th className="border border-blue-500 px-3 py-3 text-center text-xs font-black uppercase tracking-wide">
                      Uniforme
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {group.matches.map((match) => {
                    const nMatchImpreso = resolveMatchNumber(match);

                    const hora = match.time_match
                      ? moment(match.time_match, "HH:mm:ss").format("HH:mm")
                      : "";

                    // FTR-0007: celdas editables en modo edición de tabla.
                    const draftRow = inlineDraft[match.idmatch];
                    const editableCellClass = (field) =>
                      [
                        "w-full rounded-md border px-2 py-1 text-center text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400",
                        isFieldDirty(match, draftRow, field)
                          ? "border-amber-400 bg-amber-50"
                          : "border-blue-300 bg-blue-50/40",
                      ].join(" ");

                    return (
                      <tr
                        key={match.idmatch}
                        onClick={() => {
                          // En modo edición la fila no abre el modal (FTR-0007).
                          if (!inlineEdit) openMatchModal(match);
                        }}
                        className={`border-b border-slate-200 transition odd:bg-white even:bg-slate-50 ${inlineEdit ? "" : "cursor-pointer hover:bg-blue-50"
                          }`}
                        title={inlineEdit ? "Edición de tabla activa" : "Click para editar el partido"}
                      >
                        <td className="border border-slate-200 px-3 py-3 text-center font-black text-slate-800">
                          {inlineEdit ? (
                            // FTR-0033: marca el partido (por idmatch) para la acción masiva de la jornada.
                            <label
                              className="inline-flex cursor-pointer items-center justify-center gap-2"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={selectedMatchIds.includes(match.idmatch)}
                                onChange={() => toggleMatchSelection(match.idmatch)}
                                className="h-4 w-4 cursor-pointer rounded border-slate-300 text-indigo-700 focus:ring-indigo-500"
                                aria-label={`Seleccionar partido ${match.nro_match || match.idmatch}`}
                              />
                              <span>{nMatchImpreso || "-"}</span>
                            </label>
                          ) : (
                            nMatchImpreso || "-"
                          )}
                        </td>

                        <td className="border border-slate-200 px-3 py-3 text-center font-bold text-slate-700">
                          {inlineEdit ? (
                            <input
                              type="date"
                              value={draftRow?.date ?? ""}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => onInlineCellChange(match.idmatch, "date", e.target.value)}
                              className={`min-w-[9.5rem] ${editableCellClass("date")}`}
                              aria-label={`Fecha del partido ${match.nro_match || match.idmatch}`}
                            />
                          ) : (
                            formatFecha(match.date_match) || "-"
                          )}
                        </td>

                        <td className="border border-slate-200 px-3 py-3 text-center font-bold text-slate-700">
                          {inlineEdit ? (
                            <input
                              type="time"
                              value={draftRow?.time ?? ""}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => onInlineCellChange(match.idmatch, "time", e.target.value)}
                              className={`min-w-[6.5rem] ${editableCellClass("time")}`}
                              aria-label={`Hora del partido ${match.nro_match || match.idmatch}`}
                            />
                          ) : (
                            hora || "-"
                          )}
                        </td>

                        {courtsEnabled && (
                          <td className="border border-slate-200 px-3 py-3 text-center font-bold text-slate-700">
                            {inlineEdit ? (
                              <select
                                value={draftRow?.idcancha ?? ""}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => onInlineCellChange(match.idmatch, "idcancha", e.target.value)}
                                disabled={courtOptionsForMatch(match).length === 0}
                                className={`min-w-[8rem] ${editableCellClass("idcancha")}`}
                                title={
                                  courtOptionsForMatch(match).length === 0
                                    ? "La sede del partido no tiene canchas activas"
                                    : "Cancha dentro de la sede del partido"
                                }
                                aria-label={`Cancha del partido ${match.nro_match || match.idmatch}`}
                              >
                                <option value="">Sin cancha</option>
                                {courtOptionsForMatch(match).map((court) => (
                                  <option key={court.id} value={String(court.id)}>
                                    {court.name}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              courtNameForMatch(match) || "-"
                            )}
                          </td>
                        )}

                        <td className="border border-slate-200 px-3 py-3 text-center font-black text-slate-700">
                          {getMatchGroupText(match)}
                        </td>

                        <td className="border border-slate-200 px-3 py-3 text-center font-bold text-slate-700">
                          {inlineEdit && !uniformTextReadOnly ? (
                            <input
                              type="text"
                              maxLength={64}
                              value={draftRow?.uniforme1 ?? ""}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => onInlineCellChange(match.idmatch, "uniforme1", e.target.value)}
                              className={`min-w-[6rem] ${editableCellClass("uniforme1")}`}
                              placeholder="-"
                              aria-label={`Uniforme equipo 1 del partido ${match.nro_match || match.idmatch}`}
                            />
                          ) : (
                            <span className="inline-flex items-center justify-center gap-1">
                              <TbShirtFilled className="h-4 w-4" />
                              {match.uniforme1 || "-"}
                            </span>
                          )}
                        </td>

                        {/* FTR-0028 WG6: antes del sorteo se muestra el slot ("A1"), no "Por Definir":
                            es la información que el operador necesita para programar. */}
                        <td className="border border-slate-200 px-3 py-3 text-center font-black text-slate-900">
                          {renderSideName(match, 1)}
                        </td>

                        <td className="border border-slate-200 bg-slate-100 px-3 py-3 text-center font-black text-slate-800">
                          vs
                        </td>

                        <td className="border border-slate-200 px-3 py-3 text-center font-black text-slate-900">
                          {renderSideName(match, 2)}
                        </td>

                        <td className="border border-slate-200 px-3 py-3 text-center font-bold text-slate-700">
                          {inlineEdit && !uniformTextReadOnly ? (
                            <input
                              type="text"
                              maxLength={64}
                              value={draftRow?.uniforme2 ?? ""}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => onInlineCellChange(match.idmatch, "uniforme2", e.target.value)}
                              className={`min-w-[6rem] ${editableCellClass("uniforme2")}`}
                              placeholder="-"
                              aria-label={`Uniforme equipo 2 del partido ${match.nro_match || match.idmatch}`}
                            />
                          ) : (
                            <span className="inline-flex items-center justify-center gap-1">
                              <TbShirtFilled className="h-4 w-4" />
                              {match.uniforme2 || "-"}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // FTR-0040: barra discreta del filtro, solo en la vista tabla.
  const renderInstitutionFilterBar = () => (
    <div className="flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 shadow-sm sm:flex-row sm:items-center sm:justify-end">
      {institutionFilterActive ? (
        <span className="text-xs font-semibold text-slate-500 sm:mr-auto">
          Filtro activo: se muestra todo el recorrido; las acciones de jornada están deshabilitadas.
        </span>
      ) : null}
      <label
        htmlFor="programacion-institution-filter"
        className="text-xs font-bold uppercase tracking-wide text-slate-600"
      >
        Instituciones
      </label>
      <div title={inlineEdit ? "Termina la edición de la tabla para filtrar por instituciones." : undefined}>
        <InstitutionMultiSelect
          options={institutionOptions}
          value={selectedInstitutionIds}
          onChange={handleInstitutionSelectionChange}
          onMenuOpen={handleInstitutionMenuOpen}
          disabled={inlineEdit}
          isLoading={!filterParticipantsLoaded || (tournamentLoading && !tournamentLoaded)}
        />
      </div>
    </div>
  );

  const renderInstitutionFilteredTable = () => {
    if (tournamentLoading && !tournamentLoaded) {
      return (
        <div className="w-full rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-2xl font-semibold text-gray-700 text-center">Cargando Enfrentamientos{dots}</h2>
          <div className="mt-6 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-10 animate-pulse rounded-lg bg-slate-100" />
            ))}
          </div>
        </div>
      );
    }

    const includePhase = shouldIncludePhaseInScope();
    const rows = filterMatchesByInstitutions(tournamentMatches, selectedInstitutionIds);

    if (rows.length === 0) {
      return (
        <div className="w-full rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <p className="text-lg font-bold text-slate-700">No hay partidos para la selección.</p>
        </div>
      );
    }

    // N° de la jornada COMPLETA de cada partido (nunca del subconjunto filtrado).
    const printedNumbers = buildTournamentPrintedNumbers(tournamentMatches, { includePhase });
    const groups = groupMatchesByStage(rows, {
      includePhase,
      stageLabels: etapas,
      hasRepechaje: isHaveRepechaje,
    });
    const columnCount = courtsEnabled ? 11 : 10;
    const headerCell = "border border-blue-500 px-3 py-3 text-center text-xs font-black uppercase tracking-wide";
    const cell = "border border-slate-200 px-3 py-3 text-center font-bold text-slate-700";

    return (
      <div className="w-full space-y-5">
        <div className="flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-sm font-black uppercase tracking-wide text-slate-700">
            Recorrido de las instituciones seleccionadas
          </h3>
          <span className="inline-flex w-fit items-center rounded-full bg-slate-200 px-3 py-1 text-xs font-black text-slate-700">
            {rows.length} partido{rows.length === 1 ? "" : "s"} · todo el recorrido
          </span>
        </div>

        <div className="w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg">
          <div className="w-full overflow-x-auto">
            <table className="min-w-[1080px] w-full border-collapse text-sm" aria-label="Partidos de las instituciones seleccionadas">
              <thead>
                <tr style={{ backgroundColor: "#2564eb" }} className="text-white">
                  <th className={headerCell}>N°</th>
                  <th className={headerCell}>Fecha</th>
                  <th className={headerCell}>Hora</th>
                  {courtsEnabled && <th className={headerCell}>Cancha</th>}
                  <th className={headerCell}>Grupo</th>
                  <th className={headerCell}>Uniforme</th>
                  <th colSpan={3} className={headerCell}>Partido</th>
                  <th className={headerCell}>Uniforme</th>
                  <th className={headerCell}>Sede</th>
                </tr>
              </thead>

              <tbody>
                {groups.map((group) => (
                  <React.Fragment key={group.key}>
                    <tr className="bg-slate-100">
                      <td
                        colSpan={columnCount}
                        className="border border-slate-200 px-3 py-2 text-left text-xs font-black uppercase tracking-wider text-slate-700"
                      >
                        {group.label}
                      </td>
                    </tr>

                    {group.matches.map((match) => {
                      const printed = printedNumbers.get(match.idmatch);
                      const hora = match.time_match ? moment(match.time_match, "HH:mm:ss").format("HH:mm") : "";
                      const phaseIndex = Number(match.idtypephase) === 2 ? 1 : 0;

                      return (
                        <tr
                          key={match.idmatch}
                          onClick={() => openMatchModal(match)}
                          className="cursor-pointer border-b border-slate-200 transition odd:bg-white even:bg-slate-50 hover:bg-blue-50"
                          title="Click para editar el partido"
                        >
                          <td className="border border-slate-200 px-3 py-3 text-center font-black text-slate-800">
                            {printed || "-"}
                          </td>
                          <td className={cell}>{formatFecha(match.date_match) || "-"}</td>
                          <td className={cell}>{hora || "-"}</td>
                          {courtsEnabled && <td className={cell}>{courtNameForMatch(match) || "-"}</td>}
                          <td className="border border-slate-200 px-3 py-3 text-center font-black text-slate-700">
                            {getMatchGroupText(match, phaseIndex)}
                          </td>
                          <td className={cell}>
                            <span className="inline-flex items-center justify-center gap-1">
                              <TbShirtFilled className="h-4 w-4" />
                              {match.uniforme1 || "-"}
                            </span>
                          </td>
                          <td className="border border-slate-200 px-3 py-3 text-center font-black text-slate-900">
                            {renderSideName(match, 1)}
                          </td>
                          <td className="border border-slate-200 bg-slate-100 px-3 py-3 text-center font-black text-slate-800">
                            vs
                          </td>
                          <td className="border border-slate-200 px-3 py-3 text-center font-black text-slate-900">
                            {renderSideName(match, 2)}
                          </td>
                          <td className={cell}>
                            <span className="inline-flex items-center justify-center gap-1">
                              <TbShirtFilled className="h-4 w-4" />
                              {match.uniforme2 || "-"}
                            </span>
                          </td>
                          <td className={cell}>{match.sede?.name?.trim() || "Sin sede asignada"}</td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="container_programacion mx-auto p-4 font-quickSand">
        <div className="mb-4 flex justify-center">
          <div className="inline-flex rounded-lg border border-slate-200 bg-gray-100 p-1">
            <button
              type="button"
              onClick={() => setView("programacion")}
              className={`rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${view === "programacion"
                  ? "bg-white text-indigo-700 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
                }`}
            >
              Programación
            </button>
            <button
              type="button"
              onClick={() =>
                guardInlineThen(() => {
                  // FTR-0040: el filtro vive solo mientras se ve la tabla.
                  clearInstitutionFilter();
                  setView("designaciones");
                })
              }
              className={`rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${view === "designaciones"
                  ? "bg-white text-indigo-700 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
                }`}
            >
              Designaciones
            </button>
          </div>
        </div>

        {view === "designaciones" ? (
          <DesignacionesTab
            idevent={idevent}
            idsport={idsport}
            nroFechasGrupo={nroFechasGrupo}
            nroEtapasFinal={nroEtapasFinal}
            fechas={fechas}
            etapas={etapas}
            isHaveRepechaje={isHaveRepechaje}
          />
        ) : (
          <>
        <Tab.Group
          selectedIndex={selectedIndex}
          onChange={(index) => {
            // FTR-0007: cambiar de fase descarta (con confirmación) los cambios inline.
            guardInlineThen(() => {
              setSelectedIndex(index);
              setSelectedDate("1");
            });
          }}
        >
          <Tab.List
            className={`flex justify-center space-x-1 rounded-lg bg-gray-100 p-1 ${institutionFilterActive ? "opacity-50" : ""}`}
            title={institutionFilterActive ? "Filtro de instituciones activo: se muestra todo el recorrido." : undefined}
          >
            {nroFechasGrupo > 0 && (
              <Tab
                disabled={institutionFilterActive}
                className={({ selected }) =>
                  selected
                    ? "tab_selected w-full py-2.5 text-1.5xl font-medium leading-5"
                    : "tab_unselected w-full py-2.5 text-1.5xl font-medium leading-5"
                }
              >
                {+idevent === 208 ? "Partidos" : "Fase de Grupos"}
              </Tab>
            )}

            {nroEtapasFinal > 0 && (
              <Tab
                disabled={institutionFilterActive}
                className={({ selected }) =>
                  selected
                    ? "tab_selected w-full py-2.5 text-sm font-medium leading-5"
                    : "tab_unselected w-full py-2.5 text-sm font-medium leading-5"
                }
              >
                Fase de Eliminación
              </Tab>
            )}
          </Tab.List>

          <Tab.Panels className="mt-2">
            {nroFechasGrupo > 0 && (
              <Tab.Panel>
                <div className="title-container mb-4">
                  <h4 className="title text-center text-3xl font-semibold text-gray-700">FECHAS</h4>
                </div>

                <div className={`flex justify-center mb-3 space-x-2 ${institutionFilterActive ? "opacity-50" : ""}`}>
                  {fechas.map((f) => (
                    <button
                      key={f}
                      disabled={institutionFilterActive}
                      title={institutionFilterActive ? "Filtro de instituciones activo: se muestra todo el recorrido." : undefined}
                      className={`fechas_button ${selectedDate === f ? "selected_date" : "unselected_date"
                        } ${institutionFilterActive ? "cursor-not-allowed" : ""}`}
                      onClick={() => guardInlineThen(() => setSelectedDate(f))}
                    >
                      <span className="tag_fecha">Fecha</span> {f}
                    </button>
                  ))}
                </div>
              </Tab.Panel>
            )}

            {nroEtapasFinal > 0 && (
              <Tab.Panel>
                <div className="title-container mb-4">
                  <h4 className="title text-center text-3xl font-semibold text-gray-700">ETAPAS</h4>
                </div>

                <div className={`flex justify-center mb-3 space-x-2 ${institutionFilterActive ? "opacity-50" : ""}`}>
                  {etapas.map((et, i) => (
                    <button
                      key={i}
                      disabled={institutionFilterActive}
                      title={institutionFilterActive ? "Filtro de instituciones activo: se muestra todo el recorrido." : undefined}
                      className={`fechas_button ${selectedDate === String(i + 1) ? "selected_date" : "unselected_date"
                        } ${institutionFilterActive ? "cursor-not-allowed" : ""}`}
                      onClick={() => guardInlineThen(() => setSelectedDate(String(i + 1)))}
                    >
                      {isHaveRepechaje && selectedDate === "1" && i === 0 ? "Repechaje" : et}
                    </button>
                  ))}
                </div>
              </Tab.Panel>
            )}
          </Tab.Panels>
        </Tab.Group>

        <div className="w-full mb-1 flex flex-col md:items-center md:justify-center gap-3 md:gap-6">
          <div
            className="w-full md:w-auto flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3"
            style={{ marginBottom: "-15px" }}
          >
            <label className="text-sm font-semibold text-gray-700">Estado publicación:</label>

            <select
              value={publishedValue}
              onChange={(e) => setPublishedValue(e.target.value)}
              disabled={institutionFilterActive}
              title={institutionFilterActive ? filterLockTitle : undefined}
              className="w-full sm:w-40 md:w-44 px-3 py-2 border rounded-md focus:outline-none focus:ring disabled:opacity-60"
            >
              <option value="0">Borrador</option>
              <option value="1">Oficial</option>
            </select>

            <button
              onClick={handleSavePublished}
              disabled={savingPublished || loading || matches.length === 0 || institutionFilterActive}
              title={institutionFilterActive ? filterLockTitle : undefined}
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold px-4 py-2 rounded-md"
            >
              {savingPublished ? "Guardando..." : "Guardar"}
            </button>
          </div>

          <div className="w-100 flex flex-col items-center mb-2 gap-3">
            <div className="w-full flex flex-col md:flex-row items-stretch md:items-center gap-3">
              <button
                onClick={handleDownloadProgramacionAdmin}
                disabled={isDownloading || institutionFilterActive}
                className={`flex-1 md:flex-none inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 active:bg-red-800 text-white font-semibold tracking-wide px-5 py-2.5 rounded-lg shadow-md transition transform hover:-translate-y-0.5 active:translate-y-0 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2 ${isDownloading || institutionFilterActive ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                title={institutionFilterActive ? filterLockTitle : "Descargar programación (PDF)"}
              >
                {isDownloading ? (
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 5v13m0 0l-4-4m4 4l4-4M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2"
                    />
                  </svg>
                )}

                <span className="whitespace-normal text-center">
                  {isDownloading ? "Descargando..." : "Descargar Programación (PDF)"}
                </span>
              </button>

              {smartUniformsEnabled ? (
                // FTR-0041: un único botón; el tono lo propone el algoritmo en el modal (vista previa → aplicar).
                // FTR-0046: mismo aspecto para todos los roles; las versiones se gestionan dentro del modal.
                <button
                  onClick={openAutoAssignIfAllowed}
                  disabled={isAssigning || loading || institutionFilterActive}
                  className={`flex-none inline-flex items-center justify-center gap-1.5
                  px-3 py-1.5 text-sm font-medium rounded-md
                  bg-indigo-700 text-white ring-1 ring-inset ring-indigo-200
                  hover:bg-indigo-600 active:bg-indigo-200
                  shadow-sm transition whitespace-nowrap
                  disabled:opacity-50 disabled:cursor-not-allowed`}
                  title={
                    institutionFilterActive
                      ? filterLockTitle
                      : "Proponer y asignar camisetas del torneo o de una fase (grupos: por jugarse y no publicados; fases finales: solo no iniciados, publicados o no)"
                  }
                >
                  <TbShirtFilled className="h-4 w-4" />
                  <span className="hidden sm:inline">Asignar Camisetas</span>
                </button>
              ) : (
              <>
              <fieldset className="flex-1 md:flex-none rounded-lg border border-gray-200 px-3 py-2">
                <legend className="text-xs font-semibold text-gray-600 px-1">Oscuro</legend>

                <div className="flex items-center justify-center gap-4">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="oscuroPara"
                      checked={Number(localParam) === 2}
                      onChange={() => setLocalParam(2)}
                      disabled={institutionFilterActive}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm">Local</span>
                  </label>

                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="oscuroPara"
                      checked={Number(localParam) === 1}
                      onChange={() => setLocalParam(1)}
                      disabled={institutionFilterActive}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm">Visitante</span>
                  </label>
                </div>
              </fieldset>

              <button
                onClick={openConfirmIfAllowed}
                // Mientras se consulta el flag no se ofrece ningún flujo masivo (nunca dos UX a la vez).
                disabled={isAssigning || loading || matches.length === 0 || institutionFilterActive || uniformOptions.loading}
                className={`flex-none inline-flex items-center justify-center gap-1.5
                  px-3 py-1.5 text-sm font-medium rounded-md
                  bg-indigo-700 text-white ring-1 ring-inset ring-indigo-200
                  hover:bg-indigo-600 active:bg-indigo-200
                  shadow-sm transition whitespace-nowrap
                  disabled:opacity-50 disabled:cursor-not-allowed`}
                title={institutionFilterActive ? filterLockTitle : "Asignar camisetas a los partidos visibles"}
              >
                <TbShirtFilled className="h-4 w-4" />
                <span className="hidden sm:inline">{isAssigning ? "Asignando…" : "Asignar Camisetas"}</span>
              </button>
              </>
              )}

              <label
                className={[
                  "flex-none inline-flex items-center justify-center gap-3 rounded-lg border px-3 py-2",
                  "bg-white shadow-sm transition cursor-pointer",
                  tableView ? "border-[#2564eb] ring-1 ring-blue-200" : "border-gray-200 hover:border-gray-300",
                ].join(" ")}
                title="Cambiar entre vista de tarjetas y vista de tabla"
              >
                <span className="text-sm font-bold text-slate-700">Vista tabla</span>

                <span
                  className={[
                    "relative inline-flex h-6 w-11 items-center rounded-full transition",
                    tableView ? "bg-[#2564eb]" : "bg-slate-300",
                  ].join(" ")}
                >
                  <input
                    type="checkbox"
                    checked={tableView}
                    onChange={(e) => {
                      const next = e.target.checked;
                      // FTR-0007: salir de la vista tabla descarta (con confirmación) los cambios inline.
                      if (!next) {
                        guardInlineThen(() => {
                          // FTR-0040: el filtro vive solo mientras se ve la tabla.
                          clearInstitutionFilter();
                          setTableView(false);
                        });
                      } else {
                        setTableView(true);
                      }
                    }}
                    className="sr-only"
                  />

                  <span
                    className={[
                      "inline-block h-5 w-5 transform rounded-full bg-white shadow transition",
                      tableView ? "translate-x-5" : "translate-x-1",
                    ].join(" ")}
                  />
                </span>
              </label>
            </div>

            {renderTipoUbiRadios()}
          </div>
        </div>

        {tableView ? (
          <div className="mt-4 space-y-3">
            {renderInstitutionFilterBar()}
            {institutionFilterActive ? renderInstitutionFilteredTable() : renderProgramacionTable()}
          </div>
        ) : (
          <div className="cards_container grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {renderProgramacionCards()}
          </div>
        )}
          </>
        )}
      </div>

      {open && selectedMatch && (
        <ModalProgramacion
          open={open}
          setOpen={setOpen}
          match={selectedMatch}
          refreshMatches={refreshAfterMatchSave}
        />
      )}

      {smartUniformsEnabled && (
        <AssignUniformsModal
          open={autoAssignOpen}
          onClose={() => setAutoAssignOpen(false)}
          idevent={idevent}
          idsport={idsport}
          onApplied={handleAutoAssignApplied}
        />
      )}

      {confirmOpen && (
        <ConfirmAssignModal
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          onConfirm={async () => {
            await handleAssignUniforms();
            setConfirmOpen(false);
          }}
          isAssigning={isAssigning}
          count={matches?.length || 0}
          localParam={localParam}
          faseLabel={getCurrentPhaseLabel()}
        />
      )}
    </>
  );
};

export default ProgramacionArea;